import type {
  HsDisposable,
  HsFormatDecodeContext,
  HsFormatDecoder,
  HsMaterialView,
  HsVertexColorGroupView,
  HsViewerPlugin,
  HsViewerPluginContext,
} from '@sibe/hyperscene';

export const HS_MATERIALS_V1_CONTENT_TYPE: string = 'application/vnd.hyperscene.materials.v1';
const UUID_PATTERN: RegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX: readonly string[] = Array.from({ length: 256 }, (_: unknown, value: number) =>
  value.toString(16).padStart(2, '0')
);

export function hsDecodeBinaryMaterials(buffer: ArrayBuffer, requestedIds: readonly string[]): HsMaterialView[] {
  const bytes: Uint8Array = new Uint8Array(buffer);
  const data: DataView = new DataView(buffer);
  let offset: number = 0;
  const requireBytes = (count: number): void => {
    if (count < 0 || count > bytes.length - offset) {
      throw new Error('Invalid binary Materials response: truncated data.');
    }
  };
  const readInt = (): number => {
    requireBytes(4);
    const value: number = data.getInt32(offset, true);
    offset += 4;
    return value;
  };
  const readCount = (): number => {
    const value: number = readInt();
    if (value < 0) {
      throw new Error('Invalid binary Materials response: negative count.');
    }
    return value;
  };
  requireBytes(16);
  if (bytes[0] !== 72 || bytes[1] !== 83 || bytes[2] !== 77 || bytes[3] !== 84) {
    throw new Error('Invalid binary Materials response: signature.');
  }
  offset = 4;
  if (readInt() !== 1) {
    throw new Error('Unsupported binary Materials response version.');
  }
  const materialCount: number = readCount();
  const colorCount: number = readCount();
  if (materialCount > requestedIds.length || (materialCount > 0 && colorCount === 0)) {
    throw new Error('Invalid binary Materials response: material or color count.');
  }
  requireBytes(colorCount * 32 + materialCount * 13);
  const colors: Float64Array = new Float64Array(colorCount * 4);
  for (let index: number = 0; index < colors.length; index++) {
    const value: number = data.getFloat64(offset, true);
    offset += 8;
    if (!Number.isFinite(value)) {
      throw new Error('Invalid binary Materials response: non-finite color.');
    }
    colors[index] = value;
  }
  const readColor = (): number[] => {
    const index: number = readInt();
    if (index < 0 || index >= colorCount) {
      throw new Error('Invalid binary Materials response: color index.');
    }
    const start: number = index * 4;
    return [colors[start], colors[start + 1], colors[start + 2], colors[start + 3]];
  };
  const readUuid = (): string => {
    requireBytes(16);
    let value: string = '';
    for (let index: number = 0; index < 16; index++) {
      if (index === 4 || index === 6 || index === 8 || index === 10) {
        value += '-';
      }
      value += HEX[bytes[offset++]];
    }
    return value;
  };
  const materials: HsMaterialView[] = new Array<HsMaterialView>(materialCount);
  for (let row: number = 0; row < materialCount; row++) {
    const ordinal: number = readInt();
    if (ordinal < 0 || ordinal >= requestedIds.length || !UUID_PATTERN.test(requestedIds[ordinal])) {
      throw new Error('Invalid binary Materials response: request ID ordinal.');
    }
    requireBytes(1);
    const flags: number = bytes[offset++];
    if ((flags & ~3) !== 0) {
      throw new Error('Invalid binary Materials response: texture flags.');
    }
    const textureId: string | null = (flags & 1) !== 0 ? readUuid() : null;
    const colorMaskTextureId: string | null = (flags & 2) !== 0 ? readUuid() : null;
    const rgba: number[] = readColor();
    const groupCount: number = readCount();
    requireBytes(groupCount * 12);
    const vertexColorGroups: HsVertexColorGroupView[] = new Array<HsVertexColorGroupView>(groupCount);
    for (let group: number = 0; group < groupCount; group++) {
      vertexColorGroups[group] = {
        color: readColor(),
        startVertexIndex: readInt(),
        endVertexIndex: readInt(),
      };
    }
    materials[row] = {
      id: requestedIds[ordinal].toLowerCase(),
      textureId,
      colorMaskTextureId,
      rgba,
      vertexColorGroups,
    };
  }
  if (offset !== bytes.length) {
    throw new Error('Invalid binary Materials response: trailing data.');
  }
  return materials;
}

/**
 * Materials v1 responses as a format plugin. The payload refers to materials by their position in the
 * request, so `context.request` must be the requested material ids in request order.
 */
export class HsMaterialsFormat implements HsFormatDecoder<HsMaterialView[]>, HsViewerPlugin {
  public readonly id: string = 'hyperscene-materials';
  public readonly name: string = 'formats/materials';
  public readonly contentTypes: readonly string[] = [HS_MATERIALS_V1_CONTENT_TYPE];

  public install(context: HsViewerPluginContext): HsDisposable {
    return context.formats.register(this);
  }

  public decode(buffer: ArrayBuffer, context: HsFormatDecodeContext): HsMaterialView[] {
    if (!Array.isArray(context.request)) {
      throw new Error('Materials v1 decoding needs the requested material ids as context.request.');
    }
    return hsDecodeBinaryMaterials(buffer, context.request as readonly string[]);
  }
}
